import { api, request } from '../client'
import type {
  AuthSession,
  ExerciseAlias,
  LoginRequest,
  MuscleReference,
  Readiness,
  Sex,
} from '../types'

/** `POST /auth/login`. Pubblico: nessun token da allegare. */
export function login(body: LoginRequest): Promise<AuthSession> {
  return request<AuthSession>('/auth/login', { method: 'POST', body, auth: false })
}

/**
 * `POST /auth/refresh`. I refresh token sono a **uso singolo e ruotati**: la
 * chiamata invalida quello presentato e restituisce una nuova coppia.
 *
 * Con `refreshTransport: 'cookie'` il token non va nel corpo — lo allega il browser
 * e il backend risponde con un nuovo `Set-Cookie`.
 */
export function refresh(refreshToken?: string): Promise<AuthSession> {
  return request<AuthSession>('/auth/refresh', {
    method: 'POST',
    auth: false,
    ...(refreshToken ? { body: { refreshToken } } : { body: {} }),
  })
}

/**
 * `POST /auth/logout`. Va chiamato **prima** di cancellare i token locali: senza,
 * il refresh token resta valido lato server per 30 giorni.
 *
 * `accessToken` serve nell'unico caso in cui il bearer non è nello store: la revoca
 * di una sessione appena emessa e mai applicata, cioè un login riuscito ma con ruolo
 * insufficiente per il pannello.
 */
export function logout(
  refreshToken?: string,
  options: { allSessions?: boolean; accessToken?: string } = {},
): Promise<void> {
  return api.post<void>(
    '/auth/logout',
    {
      ...(refreshToken ? { refreshToken } : {}),
      ...(options.allSessions ? { allSessions: true } : {}),
    },
    options.accessToken ? { bearerToken: options.accessToken } : {},
  )
}

/** `GET /reference/muscles`. Etichette e colori dei gruppi, aggiornabili dal server. */
export function getMuscleReference(): Promise<MuscleReference> {
  return api.get<MuscleReference>('/reference/muscles')
}

/**
 * `GET /health/ready`. Pubblico.
 *
 * Il `503` è una risposta **attesa**, non un errore: porta lo stesso schema
 * `Readiness` del `200`, con l'indicazione di quale dipendenza è degradata o giù.
 * `throwOnError: false` la fa arrivare alla UI invece di trasformarla in un generico
 * «backend non raggiungibile», che è l'unica cosa che l'operatore non può usare.
 */
export function getReadiness(): Promise<Readiness> {
  return api.get<Readiness>('/health/ready', { auth: false, throwOnError: false })
}

/**
 * `GET /health` — la **liveness**, che non è la readiness.
 *
 * `/health/ready` dice «il servizio può servire richieste», e per accorgersene interroga
 * database e cache: una dipendenza giù la fa rispondere `503`. `/health` dice solo «il
 * processo è in piedi», e resta `200` mentre il database è giù.
 *
 * La differenza è quella che serve a leggere una diagnosi: entrambe rosse significa «il
 * backend non c'è»; questa verde e l'altra rossa significa «il backend c'è e una sua
 * dipendenza no», che è una telefonata diversa da fare.
 */
export function getLiveness(): Promise<{ status: string; version?: string }> {
  return api.get<{ status: string; version?: string }>('/health', {
    auth: false,
    throwOnError: false,
  })
}

/**
 * `GET /reference/exercise-aliases` — la tabella con cui il Coach riconosce i nomi che i
 * personal trainer usano davvero: «panca piana» → «Barbell Bench Press».
 *
 * È il rovescio di `unmatchedRateLast7Days` in `/admin/stats`: quella metrica dice **che**
 * il riconoscimento sta peggiorando, questa tabella è l'unico posto in cui si può vedere
 * **cosa** riconosce oggi. Senza, la metrica è un allarme senza uno schermo su cui
 * guardare.
 */
export function getExerciseAliases(signal?: AbortSignal): Promise<{ data: ExerciseAlias[] }> {
  return api.get<{ data: ExerciseAlias[] }>('/reference/exercise-aliases', {
    ...(signal ? { signal } : {}),
  })
}

/**
 * `POST /auth/password/change` — cambia la password conoscendo quella attuale.
 *
 * ⚠️ **Non c'era una schermata**, e la conseguenza non è cosmetica: un operatore a cui il
 * titolare ha appena creato l'accesso non poteva scegliersi una password sua, e chi sospettava
 * che qualcuno l'avesse vista non aveva un gesto per cambiarla. Vedi §8bis del piano.
 *
 * `keepCurrentSession: true` — le **altre** sessioni cadono, questa resta. È il verso giusto:
 * chi cambia la password lo fa spesso proprio perché teme che qualcun altro sia dentro, e
 * buttare fuori anche sé stesso trasformerebbe una precauzione in una disconnessione.
 */
export function changePassword(input: {
  currentPassword: string
  newPassword: string
}): Promise<void> {
  return api.post<void>('/auth/password/change', { ...input, keepCurrentSession: true })
}

/**
 * `POST /auth/register` — **pubblica**.
 *
 * Serve a un caso solo, e non è la registrazione libera al pannello: chi riceve un invito
 * a lavorare in una palestra e **non ha ancora un account**. Il flusso dell'invito è
 * «autentica, accetta, applica», e senza questa rotta il primo passo era impossibile per
 * chi non era già utente di Gymmi — cioè per la maggioranza dei nuovi operatori.
 *
 * Il pannello non la offre da nessun'altra parte: nessuno si registra qui per curiosità,
 * perché un account senza appartenenza non entra (`applyLoginSession` lo respinge). È
 * l'invito a dare senso all'account, e per questo la registrazione vive **dentro** quella
 * pagina e non accanto al login.
 *
 * `profile.name` è obbligatorio quando il profilo c'è: è il nome che comparirà accanto
 * alle azioni nel registro, e chiederlo dopo significherebbe non averlo mai.
 */
export function register(body: {
  email: string
  password: string
  profile?: { name: string; sex?: Sex }
}): Promise<AuthSession> {
  return request<AuthSession>('/auth/register', {
    method: 'POST',
    body,
    auth: false,
    organization: false,
  })
}

/**
 * `POST /auth/password/forgot` — **pubblica**, e risponde `202` in ogni caso.
 *
 * Non dice se quell'indirizzo esiste, ed è deliberato: una risposta diversa sui due casi
 * sarebbe un oracolo per capire chi lavora qui. La schermata mostra quindi lo stesso
 * messaggio sempre — «se l'indirizzo è registrato, l'email è partita» — che è l'unica cosa
 * vera che si può dire.
 */
export function forgotPassword(email: string): Promise<void> {
  return request<void>('/auth/password/forgot', {
    method: 'POST',
    body: { email },
    auth: false,
    organization: false,
  })
}

/**
 * `POST /auth/password/reset` — **pubblica**, con il token dell'email.
 *
 * Chiude il giro di `forgotPassword`. Il token è a uso singolo e scade: token inesistente,
 * già speso e scaduto sono lo stesso errore, per la stessa ragione per cui lo sono sugli
 * inviti — distinguerli direbbe a chi prova token a caso quali esistono.
 *
 * Invalida tutte le sessioni dell'account: chi resetta la password lo fa spesso perché
 * teme che qualcun altro sia dentro.
 */
export function resetPassword(input: { token: string; newPassword: string }): Promise<void> {
  return request<void>('/auth/password/reset', {
    method: 'POST',
    body: input,
    auth: false,
    organization: false,
  })
}

/**
 * `DELETE /me` — cancella **definitivamente** l'account e tutto ciò che vi è collegato.
 *
 * Chiede la password come conferma, che è il vincolo del contract e non una scelta
 * dell'interfaccia: è l'unico modo di sapere che sta cancellando la persona e non una
 * sessione lasciata aperta su un computer del banco.
 *
 * ⚠️ **Non è la rimozione di un operatore da un'organizzazione.** Quella è
 * `DELETE /organization/members/{id}`, la fa il titolare, e lascia l'account in piedi.
 * Questa la fa la persona su sé stessa, ed è irreversibile: il titolare non può chiederla
 * per qualcun altro, e nessun altro può annullarla.
 */
export function deleteMyAccount(password: string): Promise<void> {
  return request<void>('/me', { method: 'DELETE', body: { password }, organization: false })
}
